// src/pages/Announcements.jsx
// Control Panel → Email Templates → Announcements (Super Admins only).
// Write an email in the portal's look, choose who gets it (accounts, roles,
// people), preview it, send yourself a test, then send it. Sending goes a
// few emails at a time while this page is open, with a progress bar; if the
// page is closed it pauses, and "Continue sending" picks up where it stopped.
// Sent announcements are kept, with who got them.
// 3.52.5: each draft can also be scheduled (Send later): the backend's
// 5-minute job starts it at that time and sends it by itself.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { apiCall, fmtWhen, Preview, RichText, PlaceholderPicker, usePlaceholderTarget } from '../components/EmailEditorParts';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import { holdUpdates } from '../utils/appUpdate';
import { timeZone, tzLabel, wallToIso, isoToWall } from '../utils/timeZone';

const call = (path, getToken, options) => apiCall(`/portal/email-templates/announcements${path}`, getToken, options);
const post = (path, getToken, body) => call(path, getToken, { method: 'POST', body: JSON.stringify(body) });
const EMPTY_FIELDS = { subject: '', heading: '', body: '<p>{greeting},</p><p></p>', button: '', buttonUrl: '', smallPrint: '' };
const EMPTY_AUDIENCE = { accounts: [], levels: [], people: [], exclude: [], copyToMe: false };
// 3.49.15: addresses typed or pasted into "Also send to", separated by commas, semicolons, spaces or lines
const EMAIL_RE = /^[^\s@,;<>]+@[^\s@,;<>]+\.[a-z]{2,}$/i;
const splitAddresses = t => String(t || '').split(/[\s,;]+/).map(x => x.trim().replace(/^<|>$/g, '').toLowerCase()).filter(Boolean);
const STATUS = { draft: 'Draft', sending: 'Sending', sent: 'Sent' };

// 3.52.5: a wall time ('2026-10-12T09:00') in the person's Fax Portal time
// zone → ISO, and back (for the "Send later" box)
// 3.52.5: how often a scheduled one sends
const REPEAT = { once: 'Once', weekly: 'Every week', biweekly: 'Every 2 weeks', monthly: 'Every month' };
const fmtDay = iso => new Date(iso).toLocaleDateString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', year: 'numeric' });
function repeatText(repeat, at, until) {
  if (!repeat || repeat === 'once') return 'Once';
  const d = new Date(at);
  const tz = timeZone();
  const day = Number(new Intl.DateTimeFormat('en-US', { timeZone: tz, day: 'numeric' }).format(d));
  const nth = `${day}${['th', 'st', 'nd', 'rd'][(day > 10 && day < 14) || day % 10 > 3 ? 0 : day % 10]}`;
  const when = repeat === 'monthly'
    ? `every month on the ${nth}${day > 28 ? ' (the last day in shorter months)' : ''}`
    : `${repeat === 'biweekly' ? 'every 2 weeks' : 'every week'} on ${d.toLocaleDateString('en-US', { timeZone: tz, weekday: 'long' })}`;
  return `Repeats ${when} at ${d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' })}${until ? `, until ${fmtDay(until)}` : ', until you cancel it'}`;
}
const fmtSchedule = iso => `${new Date(iso).toLocaleString('en-US', { timeZone: timeZone(), weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ${tzLabel(new Date(iso))}`;

export default function Announcements() {
  const { getToken } = useAuth();
  const [list, setList]       = useState(null);
  const [options, setOptions] = useState(null);
  const [error, setError]     = useState('');
  const [picked, setPicked]   = useState(null);   // id | 'new' | null
  const [dirty, setDirty]     = useState(false);
  const { confirm } = useNotify();
  const [autoSend, setAutoSend] = useState(null);  // id just started: send right away

  const loadList = useCallback(() => call('', getToken).then(r => { setList(r.data); return r.data; }), [getToken]);
  useEffect(() => {
    Promise.all([loadList(), call('/options', getToken).then(setOptions)]).catch(e => setError(e.message));
  }, [loadList, getToken]);

  const pick = async id => {
    if (id === picked) return;
    if (dirty && !(await confirm({ title: 'Leave without saving?', message: "Your changes to this announcement aren't saved.", confirmLabel: 'Leave without saving', cancelLabel: 'Keep editing', danger: true }))) return;
    setDirty(false); setPicked(id);
  };
  const opened = (id, { send = false } = {}) => { setDirty(false); setPicked(id); if (send) setAutoSend(id); loadList().catch(() => {}); };

  if (error) return <div role="alert" style={S.error}>{error}</div>;
  if (!list || !options) return <Loader />;
  const current = list.find(a => a.id === picked);

  return (
    <div style={S.layout}>
      <nav style={S.nav} aria-label="Announcements">
        <button onClick={() => pick('new')} style={S.newBtn}>＋ New announcement</button>
        {!list.length && <div style={S.muted}>None yet. Write one to email chosen people, for example about what's new in the portal.</div>}
        {list.map(a => (
          <button key={a.id} onClick={() => pick(a.id)} aria-current={picked === a.id ? 'true' : undefined}
            style={{ ...S.item, ...(picked === a.id ? S.itemOn : {}) }}>
            <span style={S.itemName}>{a.title}</span>
            <span style={S.itemMeta}>
              {a.fromSeries && <span style={{ ...S.pill, ...S.pill_scheduled }} title="Sent by a repeating schedule"><Emo e="🔁" /></span>}
              {a.status === 'draft' && a.scheduledFor
                ? <><span style={{ ...S.pill, ...S.pill_scheduled }}>{a.scheduleRepeat && a.scheduleRepeat !== 'once' ? <><Emo e="🔁" /> {REPEAT[a.scheduleRepeat]}</> : <><Emo e="🕒" /> Scheduled</>}</span>{a.scheduleRepeat && a.scheduleRepeat !== 'once' ? 'Next ' : ''}{fmtSchedule(a.scheduledFor)}</>
                : <><span style={{ ...S.pill, ...S[`pill_${a.status}`] }}>{STATUS[a.status]}{a.status !== 'draft' ? ` ${a.counts.sent}/${a.counts.total}` : ''}</span>
                  {fmtWhen(a.sentAt || a.startedAt || a.updatedAt)}</>}
            </span>
          </button>
        ))}
      </nav>

      <section style={S.detail}>
        {!picked && <div style={S.muted}>Pick an announcement, or start a new one.</div>}
        {(picked === 'new' || current?.status === 'draft') && (
          <DraftEditor key={picked} id={picked === 'new' ? null : picked} options={options} getToken={getToken}
            onDirty={setDirty} onOpen={opened} onDeleted={() => { setDirty(false); setPicked(null); loadList().catch(() => {}); }} />
        )}
        {current && current.status !== 'draft' && (
          <SentView key={picked} id={picked} getToken={getToken} autoSend={autoSend === picked}
            onProgress={() => loadList().catch(() => {})} onOpen={opened} />
        )}
      </section>
    </div>
  );
}

// ── Writing (drafts) ────────────────────────────────────────
function DraftEditor({ id, options, getToken, onDirty, onOpen, onDeleted }) {
  const { notify, confirm } = useNotify();
  const [loaded, setLoaded]   = useState(!id);
  const [title, setTitle]     = useState('');
  const [draft, setDraft]     = useState(EMPTY_FIELDS);
  const [audience, setAudience] = useState(EMPTY_AUDIENCE);
  const [baseline, setBaseline] = useState(null);
  const [loadKey, setLoadKey] = useState(0);
  const [live, setLive]       = useState(null);
  const [people, setPeople]   = useState(null);     // who it would go to
  const [showPeople, setShowPeople] = useState(false);
  const [busy, setBusy]       = useState('');
  const [confirming, setConfirming] = useState(false);
  // 3.52.5: Send later
  const [sched, setSched]     = useState({ at: null, error: null, repeat: null, until: null, count: 0 });
  const [laterRepeat, setLaterRepeat] = useState('once');
  const [laterUntil, setLaterUntil]   = useState('');
  const [laterOpen, setLaterOpen] = useState(false);
  const [laterWall, setLaterWall] = useState('');

  useEffect(() => {
    if (!id) { setBaseline(JSON.stringify({ title: '', draft: EMPTY_FIELDS, audience: EMPTY_AUDIENCE })); return; }
    call(`/${id}`, getToken).then(r => {
      const a = r.data;
      const f = { ...EMPTY_FIELDS, ...a.fields }, au = { ...EMPTY_AUDIENCE, ...a.audience };
      setTitle(a.title); setDraft(f); setAudience(au); setLoadKey(k => k + 1); setLoaded(true);
      setSched({ at: a.scheduledFor || null, error: a.scheduleError || null, repeat: a.scheduledFor ? (a.scheduleRepeat || 'once') : null, until: a.scheduledFor ? (a.scheduleUntil || null) : null, count: a.repeatCount || 0 });
      setBaseline(JSON.stringify({ title: a.title, draft: f, audience: au }));
    }).catch(e => notify({ tone: 'error', title: "Couldn't open the announcement", detail: e.message }));
  }, [id, getToken, notify]);

  const dirty = loaded && baseline !== null && JSON.stringify({ title, draft, audience }) !== baseline;
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);

  // Preview and recipients, a moment after changes stop
  useEffect(() => {
    if (!loaded) return undefined;
    const t = setTimeout(() => post('/render', getToken, { fields: draft }).then(setLive).catch(e => setLive({ subject: '', html: '', problems: [e.message] })), 500);
    return () => clearTimeout(t);
  }, [draft, loaded, getToken]);
  useEffect(() => {
    if (!loaded) return undefined;
    const t = setTimeout(() => post('/audience', getToken, { audience }).then(r => setPeople(r.data)).catch(() => setPeople(null)), 400);
    return () => clearTimeout(t);
  }, [audience, loaded, getToken]);

  const setField = (name, value) => setDraft(d => ({ ...d, [name]: value }));
  const target = usePlaceholderTarget(draft, setField);
  const toggle = (key, value) => setAudience(a => ({ ...a, [key]: a[key].includes(value) ? a[key].filter(x => x !== value) : [...a[key], value] }));

  async function save({ quiet = false } = {}) {
    setBusy('save');
    try {
      const body = JSON.stringify({ title, fields: draft, audience });
      const r = id ? await call(`/${id}`, getToken, { method: 'PUT', body }) : await call('', getToken, { method: 'POST', body });
      setBaseline(JSON.stringify({ title, draft, audience }));
      if (!quiet) notify({ tone: 'success', title: 'Draft saved', detail: 'Nothing has been sent.' });
      setBusy('');
      if (!id) onOpen(r.data.id);
      return r.data.id;
    } catch (e) { notify({ tone: 'error', title: "Couldn't save", detail: e.message }); setBusy(''); return null; }
  }
  async function sendTest() {
    setBusy('test');
    try {
      const r = await post('/test', getToken, { fields: draft });
      notify({ tone: 'success', title: 'Test email sent', detail: `Check ${r.sentTo}. Nobody else got it.` });
    } catch (e) { notify({ tone: 'error', title: "Couldn't send the test", detail: e.message }); }
    setBusy('');
  }
  async function sendNow() {
    const savedId = await save({ quiet: true });
    if (!savedId) return;
    setBusy('send');
    try {
      await post(`/${savedId}/start`, getToken, {});
      setConfirming(false);
      onOpen(savedId, { send: true });
    } catch (e) { notify({ tone: 'error', title: "Couldn't start sending", detail: e.message }); }
    setBusy('');
  }
  // 3.52.5: send by itself at a time (saved first)
  async function scheduleAt() {
    const at = wallToIso(laterWall, timeZone());
    if (!at) { notify({ tone: 'error', title: 'Pick a date and time' }); return; }
    const savedId = await save({ quiet: true });
    if (!savedId) return;
    setBusy('schedule');
    try {
      const r = await post(`/${savedId}/schedule`, getToken, { at, repeat: laterRepeat, until: laterRepeat !== 'once' && laterUntil ? laterUntil : null });
      const d = r.data;
      setSched({ at: d.scheduledFor, error: null, repeat: d.scheduleRepeat || 'once', until: d.scheduleUntil || null, count: d.repeatCount || 0 }); setLaterOpen(false);
      notify({ tone: 'success', title: 'Scheduled', detail: d.scheduleRepeat && d.scheduleRepeat !== 'once'
        ? `${repeatText(d.scheduleRepeat, d.scheduledFor, d.scheduleUntil)}. The first one goes on ${fmtSchedule(d.scheduledFor)}.`
        : `It sends by itself on ${fmtSchedule(d.scheduledFor)}. You can still edit it until then.` });
      onOpen(savedId);
    } catch (e) { notify({ tone: 'error', title: "Couldn't schedule it", detail: e.message }); }
    setBusy('');
  }
  async function cancelSchedule() {
    setBusy('unschedule');
    try {
      await call(`/${id}/schedule`, getToken, { method: 'DELETE' });
      setSched({ at: null, error: null, repeat: null, until: null, count: sched.count });
      notify({ tone: 'success', title: 'Schedule cancelled', detail: "It stays a draft. Nothing was sent." });
      onOpen(id);
    } catch (e) { notify({ tone: 'error', title: "Couldn't cancel the schedule", detail: e.message }); }
    setBusy('');
  }
  async function remove() {
    if (!(await confirm({ title: sched.at ? 'Delete this scheduled draft?' : 'Delete this draft?', message: sched.at ? 'It won’t be sent.' : 'Nothing was sent, so nobody is affected.', confirmLabel: 'Delete', danger: true }))) return;
    try { await call(`/${id}`, getToken, { method: 'DELETE' }); notify({ tone: 'success', title: 'Draft deleted' }); onDeleted(); }
    catch (e) { notify({ tone: 'error', title: "Couldn't delete", detail: e.message }); }
  }

  if (!loaded) return <Loader />;
  const problems = live?.problems || [];
  const count = people?.length ?? null;
  const tooMany = count !== null && count > options.maxRecipients;
  const excluded = audience.exclude;
  const nameOf = email => options.people.find(p => p.email === email)?.name || '';

  return (
    <div style={S.editor}>
      <div style={S.editCol}>
        <label style={S.label} htmlFor="an-title">Name (only you see this)</label>
        <input id="an-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. What's new in October" style={S.input} />

        <h5 style={S.section}>The email</h5>
        <label style={S.label} htmlFor="an-subject">Subject</label>
        <input id="an-subject" value={draft.subject} onChange={e => setField('subject', e.target.value)} style={S.input} {...target.inputProps('subject')} />
        <label style={S.label} htmlFor="an-heading">Heading</label>
        <input id="an-heading" value={draft.heading} onChange={e => setField('heading', e.target.value)} style={S.input} {...target.inputProps('heading')} />
        <span style={S.label} id="an-body-label">Body</span>
        <RichText key={`body-${loadKey}`} labelId="an-body-label" initial={draft.body} phMap={Object.fromEntries(options.placeholders.map(p => [p.key, p]))} minHeight={220}
          onChange={v => setField('body', v)} {...target.richProps} registerInsert={target.registerBody} />
        <div style={S.twoCol}>
          <div style={{ flex: '1 1 160px' }}>
            <label style={S.label} htmlFor="an-button">Button label (optional)</label>
            <input id="an-button" value={draft.button} onChange={e => setField('button', e.target.value)} placeholder="e.g. Open the Fax Portal" style={S.input} {...target.inputProps('button')} />
          </div>
          <div style={{ flex: '2 1 220px' }}>
            <label style={S.label} htmlFor="an-url">Button link</label>
            <input id="an-url" value={draft.buttonUrl} onChange={e => setField('buttonUrl', e.target.value)} placeholder="https://fax.crazypixels.org/portal/" style={S.input} />
          </div>
        </div>
        <span style={S.label} id="an-small-label">Small print (optional)</span>
        <RichText key={`small-${loadKey}`} labelId="an-small-label" initial={draft.smallPrint} phMap={Object.fromEntries(options.placeholders.map(p => [p.key, p]))} minHeight={50}
          onChange={v => setField('smallPrint', v)} {...target.richProps} />
        <PlaceholderPicker placeholders={options.placeholders} onPick={target.insert} />

        <h5 style={S.section}>Who gets it</h5>
        <div style={S.label}>Clients <span style={S.labelHint}>(none ticked = every client)</span></div>
        <div style={S.pills}>
          {options.accounts.map(a => (
            <label key={a.id} style={{ ...S.check, ...(audience.accounts.includes(a.id) ? S.checkOn : {}) }}>
              <input type="checkbox" checked={audience.accounts.includes(a.id)} onChange={() => toggle('accounts', a.id)} /> {a.name}
            </label>
          ))}
        </div>
        <div style={S.label}>Roles <span style={S.labelHint}>(none ticked = every role)</span></div>
        <div style={S.pills}>
          {options.levels.map(l => (
            <label key={l.id} style={{ ...S.check, ...(audience.levels.includes(l.id) ? S.checkOn : {}) }}>
              <input type="checkbox" checked={audience.levels.includes(l.id)} onChange={() => toggle('levels', l.id)} /> {l.label}s
            </label>
          ))}
        </div>
        <div style={S.label}>Also send to</div>
        <PeoplePicker all={options.people} chosen={audience.people} onAdd={e => setAudience(a => ({ ...a, people: [...a.people, e], exclude: a.exclude.filter(x => x !== e) }))}
          onRemove={e => setAudience(a => ({ ...a, people: a.people.filter(x => x !== e) }))} />

        {/* 3.49.15: one more copy, to you, when it's sent */}
        <label style={{ ...S.check, ...(audience.copyToMe ? S.checkOn : {}), marginTop: 10 }}>
          <input type="checkbox" checked={!!audience.copyToMe} onChange={e => setAudience(a => ({ ...a, copyToMe: e.target.checked }))} /> Send me a copy too
        </label>
        <div style={S.countBox}>
          <strong>{count === null ? 'Working out who gets it…' : `${count} ${count === 1 ? 'person' : 'people'} will get this`}{audience.copyToMe ? ', plus a copy to you' : ''}</strong>
          <div style={S.dim}>Each person gets their own email, addressed only to them: nobody sees who else got it.</div>
          {count > 0 && <button onClick={() => setShowPeople(v => !v)} style={S.linkBtn}>{showPeople ? 'Hide the list' : 'Show the list'}</button>}
          {tooMany && <div style={S.warn}>One announcement can go to at most {options.maxRecipients} people. Please narrow it down.</div>}
          {excluded.length > 0 && (
            <div style={S.excluded}>Left out: {excluded.map(e => (
              <button key={e} onClick={() => setAudience(a => ({ ...a, exclude: a.exclude.filter(x => x !== e) }))} style={S.chip} title="Put back">{nameOf(e) || e} ↺</button>
            ))}</div>
          )}
          {showPeople && people && (
            <div style={S.peopleList}>
              {people.map(p => (
                <div key={p.email} style={S.personRow}>
                  <span style={{ minWidth: 0 }}><strong>{p.name || p.email}</strong>{p.name && <span style={S.dim}> {p.email}</span>}<span style={S.dim}> · {[...p.accounts, levelLabel(options, p.level)].filter(Boolean).join(' · ')}</span></span>
                  <button onClick={() => setAudience(a => ({ ...a, exclude: [...a.exclude, p.email], people: a.people.filter(x => x !== p.email) }))} style={S.linkBtn}>Leave out</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {problems.length > 0 && <div role="alert" style={S.problems}>{problems.map(p => <div key={p}><Emo e="⚠️" /> {p}</div>)}</div>}

        {/* 3.52.5: Send later */}
        {sched.error && <div role="alert" style={S.problems}><Emo e="⚠️" /> The last scheduled send didn’t start: {sched.error} {sched.at ? 'It was skipped; the next one is still scheduled.' : 'It’s a plain draft again; fix it and schedule it again or send it now.'}</div>}
        {sched.at && !laterOpen && (
          <div style={S.schedBox} role="status">
            {sched.repeat && sched.repeat !== 'once' ? <>
              <div><strong><Emo e="🔁" /> {repeatText(sched.repeat, sched.at, sched.until)}</strong></div>
              <div><strong>Next: {fmtSchedule(sched.at)}</strong>{sched.count > 0 && <span style={S.dim}> · sent {sched.count} time{sched.count === 1 ? '' : 's'} so far (each one is in the list with who got it)</span>}</div>
              <div style={S.dim}>Each time, a copy goes to the people who match the recipients at that moment ({count ?? '…'} now), a few emails at a time. This draft stays: edit and save it any time and the next one uses your changes.</div>
            </> : <>
              <div><strong><Emo e="🕒" /> Scheduled for {fmtSchedule(sched.at)}</strong></div>
              <div style={S.dim}>It sends by itself then, to the people who match the recipients at that moment ({count ?? '…'} now), a few emails at a time. You can edit and save it until then. Nobody gets it before.</div>
            </>}
            <div style={S.actions}>
              <button onClick={() => { setLaterWall(isoToWall(sched.at, timeZone())); setLaterRepeat(sched.repeat || 'once'); setLaterUntil(sched.until ? isoToWall(sched.until, timeZone()).slice(0, 10) : ''); setLaterOpen(true); }} disabled={!!busy} style={S.ghostBtn}>Change the schedule</button>
              <button onClick={cancelSchedule} disabled={!!busy} style={S.ghostBtn}>{busy === 'unschedule' ? 'Cancelling…' : 'Cancel the schedule'}</button>
            </div>
          </div>
        )}
        {laterOpen && (
          <div style={S.schedBox}>
            <label style={{ fontWeight: 700 }} htmlFor="ann-later">{laterRepeat === 'once' ? 'Send on' : 'First one on'}</label>
            <input id="ann-later" type="datetime-local" value={laterWall} onChange={e => setLaterWall(e.target.value)} style={S.dateInput}
              min={isoToWall(new Date(Date.now() + 5 * 60000).toISOString(), timeZone())} />
            <div role="radiogroup" aria-label="How often" style={S.repeatRow}>
              {Object.entries(REPEAT).map(([k, label]) => (
                <button key={k} role="radio" aria-checked={laterRepeat === k} onClick={() => setLaterRepeat(k)} style={{ ...S.repeatOpt, ...(laterRepeat === k ? S.repeatOn : null) }}>{label}</button>
              ))}
            </div>
            {laterRepeat !== 'once' && <>
              <label style={{ fontWeight: 700 }} htmlFor="ann-until">Ends on <span style={{ ...S.dim, fontWeight: 400 }}>(optional; empty = until you cancel it)</span></label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input id="ann-until" type="date" value={laterUntil} onChange={e => setLaterUntil(e.target.value)} style={S.dateInput} min={laterWall.slice(0, 10) || undefined} />
                {laterUntil && <button onClick={() => setLaterUntil('')} style={S.linkBtn}>Clear</button>}
              </div>
              {wallToIso(laterWall, timeZone()) && <div style={S.dim}>{repeatText(laterRepeat, wallToIso(laterWall, timeZone()), laterUntil ? wallToIso(`${laterUntil}T23:59`, timeZone()) : null)}.</div>}
            </>}
            <div style={S.dim}>In your Fax Portal time zone ({tzLabel()}, set in My profile). It sends by itself; you don’t need to keep this page open.</div>
            <div style={S.actions}>
              <button onClick={scheduleAt} disabled={!!busy || !laterWall || problems.length > 0 || !count || tooMany} style={S.sendBtn}>{busy === 'schedule' ? 'Scheduling…' : sched.at ? 'Save the schedule' : `Schedule for ${count ?? '…'} ${count === 1 ? 'person' : 'people'}`}</button>
              <button onClick={() => setLaterOpen(false)} disabled={!!busy} style={S.ghostBtn}>Cancel</button>
            </div>
          </div>
        )}

        {confirming ? (
          <div style={S.confirm} role="alertdialog" aria-label="Confirm sending">
            <strong>Send "{draft.subject}" to {count} {count === 1 ? 'person' : 'people'}?</strong>
            <div style={S.dim}>It goes from fax@crazypixels.org, one email per person, starting now. This can't be undone. Keep this page open until it finishes.{sched.at && sched.repeat !== 'once' ? ' This sends the draft itself, so its repeating schedule ends. To keep the schedule, Duplicate it after.' : ''}</div>
            <div style={S.actions}>
              <button onClick={sendNow} disabled={!!busy} style={S.sendBtn}>{busy === 'send' ? 'Starting…' : `Yes, send to ${count}`}</button>
              <button onClick={() => setConfirming(false)} disabled={!!busy} style={S.ghostBtn}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={S.actions}>
            <button onClick={() => save()} disabled={!!busy || (!dirty && !!id)} style={S.saveBtn}>{busy === 'save' ? 'Saving…' : 'Save draft'}</button>
            <button onClick={sendTest} disabled={!!busy || problems.length > 0} style={S.ghostBtn}>{busy === 'test' ? 'Sending…' : <><Emo e="✉️" /> Send test to me</>}</button>
            <button onClick={() => setConfirming(true)} disabled={!!busy || problems.length > 0 || !count || tooMany} style={S.sendBtn}>Send to {count ?? '…'} {count === 1 ? 'person' : 'people'}…</button>
            {!sched.at && !laterOpen && <button onClick={() => { setLaterWall(isoToWall(new Date(Date.now() + 60 * 60000).toISOString(), timeZone()).slice(0, 14) + '00'); setLaterRepeat('once'); setLaterUntil(''); setLaterOpen(true); }} disabled={!!busy || problems.length > 0 || !count || tooMany} style={S.ghostBtn}><Emo e="🕒" /> Send later…</button>}
            <span style={{ flex: 1 }} />
            {id && <button onClick={remove} disabled={!!busy} style={S.dangerLink}>Delete draft</button>}
          </div>
        )}
      </div>

      <div style={S.previewCol}>
        <div style={S.liveTitle}>Preview, with your details {dirty && <span style={S.unsaved}>Not saved</span>}</div>
        {!live ? <div style={S.muted}>Building the preview…</div>
          : live.html ? <Preview subject={live.subject} html={live.html} title="Preview of the announcement" height={680} />
          : <div style={S.muted}>Fix the problems on the left to see the preview.</div>}
      </div>
    </div>
  );
}

const levelLabel = (options, id) => options.levels.find(l => l.id === id)?.label || '';

function PeoplePicker({ all, chosen, onAdd, onRemove }) {
  const [q, setQ] = useState('');
  const [bad, setBad] = useState('');
  // Several addresses at once (comma-separated, pasted from a list): each valid one is added
  function addTyped(text) {
    const parts = splitAddresses(text);
    const ok = parts.filter(e => EMAIL_RE.test(e)), wrong = parts.filter(e => !EMAIL_RE.test(e));
    ok.filter(e => !chosen.includes(e)).forEach(onAdd);
    setBad(wrong.length ? `Not an email address: ${wrong.join(', ')}` : '');
    setQ(wrong.join(', '));
    return ok.length;
  }
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return all.filter(p => !chosen.includes(p.email) && (p.email.includes(t) || p.name.toLowerCase().includes(t))).slice(0, 8);
  }, [q, all, chosen]);
  return (
    <div>
      {chosen.length > 0 && (
        <div style={S.pills}>{chosen.map(e => (
          <span key={e} style={S.chip} title={all.some(p => p.email === e) ? e : 'Not in the Fax Portal: gets the announcement on its own'}>{all.find(p => p.email === e)?.name || e}{!all.some(p => p.email === e) && <span style={S.dim}> (outside)</span>}
            <button onClick={() => onRemove(e)} aria-label={`Remove ${e}`} style={S.chipX}>×</button></span>
        ))}</div>
      )}
      <input value={q} placeholder="Search people by name or email, or paste addresses separated by commas" aria-label="Add people" style={S.input}
        onChange={e => { const v = e.target.value; if (/[,;\n]/.test(v)) addTyped(v); else { setQ(v); setBad(''); } }}
        onPaste={e => { const t = e.clipboardData.getData('text'); if (/[,;\s]/.test(t.trim())) { e.preventDefault(); addTyped(q + ' ' + t); } }}
        onKeyDown={e => { if (e.key === 'Enter' && q.trim()) { e.preventDefault(); if (!matches.length || EMAIL_RE.test(q.trim())) addTyped(q); else { onAdd(matches[0].email); setQ(''); } } }} />
      {bad && <div style={{ ...S.dim, color: 'var(--t-c62828)', marginTop: 4 }}>{bad}</div>}
      {matches.length > 0 && (
        <div style={S.matches}>
          {matches.map(p => (
            <button key={p.email} onClick={() => { onAdd(p.email); setQ(''); }} style={S.match}>
              <strong>{p.name || p.email}</strong>{p.name && <span style={S.dim}> {p.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sending and sent ────────────────────────────────────────
function SentView({ id, getToken, autoSend, onProgress, onOpen }) {
  const { notify } = useNotify();
  const [a, setA]           = useState(null);
  const [live, setLive]     = useState(null);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState('all');
  const stopRef = useRef(false);

  const load = useCallback(() => call(`/${id}`, getToken).then(r => { setA(r.data); return r.data; }), [id, getToken]);
  useEffect(() => {
    load().then(d => post('/render', getToken, { fields: d.fields }).then(setLive).catch(() => {}))
      .catch(e => notify({ tone: 'error', title: "Couldn't open the announcement", detail: e.message }));
  }, [load, getToken, notify]);

  const run = useCallback(async () => {
    stopRef.current = false;
    setRunning(true);
    try {
      for (;;) {
        if (stopRef.current) break;
        const d = (await post(`/${id}/send-batch`, getToken, {})).data;
        setA(d); onProgress();
        if (d.status !== 'sending' || !d.counts.pending) break;
        if (d.busy) await new Promise(r => setTimeout(r, 3000));
      }
    } catch (e) { notify({ tone: 'error', title: 'Sending paused', detail: `${e.message} Click "Continue sending" to carry on.` }); }
    setRunning(false);
  }, [id, getToken, notify, onProgress]);

  // Started from the editor: send straight away
  const started = useRef(false);
  useEffect(() => { if (autoSend && a?.status === 'sending' && !started.current) { started.current = true; run(); } }, [autoSend, a, run]);
  // Leaving the page while sending pauses it: warn first
  useEffect(() => {
    if (!running) return undefined;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    const release = holdUpdates();   // 3.62.30: a new version waits too
    return () => { window.removeEventListener('beforeunload', warn); release(); };
  }, [running]);
  useEffect(() => () => { stopRef.current = true; }, []);

  async function retry() {
    try { setA((await post(`/${id}/retry`, getToken, {})).data); run(); }
    catch (e) { notify({ tone: 'error', title: "Couldn't retry", detail: e.message }); }
  }
  async function duplicate() {
    try {
      const r = await call('', getToken, { method: 'POST', body: JSON.stringify({ title: `${a.title} (copy)`, fields: a.fields, audience: a.audience }) });
      notify({ tone: 'success', title: 'Copied as a new draft' });
      onOpen(r.data.id);
    } catch (e) { notify({ tone: 'error', title: "Couldn't copy", detail: e.message }); }
  }

  if (!a) return <Loader />;
  const c = a.counts;
  const pct = c.total ? Math.round(((c.sent + c.failed) / c.total) * 100) : 0;
  const rows = a.recipients.filter(r => filter === 'all' || r.status === filter);

  return (
    <div style={S.editor}>
      <div style={S.editCol}>
        <h4 style={S.sentTitle}>{a.title}</h4>
        <div style={S.dim}>Started by {a.startedBy} on {fmtWhen(a.startedAt)}{a.sentAt ? ` · finished ${fmtWhen(a.sentAt)}` : ''}</div>

        <div style={S.progressBox}>
          <div style={S.progressLine}>
            <strong>{a.status === 'sent' ? 'Sent' : running ? 'Sending…' : a.autoSend ? 'Sending by itself (scheduled)' : 'Paused'}</strong>
            <span>{c.sent} sent{c.failed ? `, ${c.failed} failed` : ''}{c.pending ? `, ${c.pending} to go` : ''} of {c.total}</span>
          </div>
          <div style={S.bar} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><div style={{ ...S.barFill, width: `${pct}%` }} /></div>
          <div style={S.actions}>
            {a.status === 'sending' && !running && !a.autoSend && <button onClick={run} style={S.sendBtn}>Continue sending</button>}
            {running && <button onClick={() => { stopRef.current = true; }} style={S.ghostBtn}>Pause</button>}
            {!running && c.failed > 0 && <button onClick={retry} style={S.ghostBtn}>Try the {c.failed} failed again</button>}
            <span style={{ flex: 1 }} />
            <button onClick={duplicate} disabled={running} style={S.linkBtn}>Duplicate as a new draft</button>
          </div>
          {running && <div style={S.dim}>Keep this page open. Closing it pauses sending; you can continue later.</div>}
          {a.status === 'sending' && a.autoSend && <div style={S.dim}>Started on schedule. It sends a few emails every 5 minutes until everyone has it; you don’t need to keep this page open.</div>}
        </div>

        <div style={S.filterRow} role="tablist" aria-label="Recipients">
          {[['all', `Everyone (${c.total})`], ['sent', `Sent (${c.sent})`], ['failed', `Failed (${c.failed})`], ['pending', `To go (${c.pending})`]].map(([k, l]) => (
            <button key={k} role="tab" aria-selected={filter === k} onClick={() => setFilter(k)} style={{ ...S.filter, ...(filter === k ? S.filterOn : {}) }}>{l}</button>
          ))}
        </div>
        <div style={S.peopleList}>
          {rows.map(r => (
            <div key={r.email} style={S.personRow}>
              <span style={{ minWidth: 0 }}><strong>{r.name || r.email}</strong>{r.name && <span style={S.dim}> {r.email}</span>}
                {r.error && <span style={S.err}> · {r.error}</span>}</span>
              <span style={{ ...S.dim, whiteSpace: 'nowrap' }}>{r.status === 'sent' ? `✓ ${fmtWhen(r.at)}` : r.status === 'failed' ? '✗ failed' : '…'}</span>
            </div>
          ))}
          {!rows.length && <div style={S.muted}>Nobody here.</div>}
        </div>
      </div>
      <div style={S.previewCol}>
        <div style={S.liveTitle}>The email (shown with your details)</div>
        {live?.html ? <Preview subject={live.subject} html={live.html} title="The announcement" height={680} /> : <div style={S.muted}>Loading…</div>}
      </div>
    </div>
  );
}

const S = {
  muted: { color: 'var(--t-90a4ae)', fontSize: 13, padding: 12 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  layout: { display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' },
  nav: { flex: '1 1 240px', maxWidth: 320, background: 'var(--c-ffffff)', borderRadius: 12, padding: 12, border: '1px solid var(--b-e8f5e9)', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', maxHeight: '75vh', overflowY: 'auto' },
  newBtn: { width: '100%', padding: '9px 12px', borderRadius: 8, border: 'none', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 10 },
  item: { display: 'flex', flexDirection: 'column', gap: 3, width: '100%', textAlign: 'left', background: 'none', border: '1px solid transparent', borderRadius: 8, padding: '7px 9px', cursor: 'pointer', fontFamily: 'inherit' },
  itemOn: { background: 'var(--c-e0f2f1)', border: '1px solid var(--c-80cbc4)' },
  itemName: { fontSize: 13, fontWeight: 600, color: 'var(--t-263238)', overflowWrap: 'anywhere' },
  itemMeta: { display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, color: 'var(--t-78909c)', flexWrap: 'wrap' },
  pill: { padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700 },
  pill_draft: { background: 'var(--c-eceff1)', color: 'var(--t-546e7a)' },
  pill_sending: { background: 'var(--c-fff3e0)', color: 'var(--t-e65100)' },
  pill_sent: { background: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)' },
  pill_scheduled: { background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)' },   // 3.52.5
  detail: { flex: '3 1 520px', minWidth: 0, background: 'var(--c-ffffff)', borderRadius: 12, padding: '16px 18px', border: '1px solid var(--b-e8f5e9)', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)' },
  editor: { display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' },
  editCol: { flex: '1 1 420px', minWidth: 0 },
  previewCol: { flex: '1 1 380px', minWidth: 0, position: 'sticky', top: 8 },
  section: { margin: '18px 0 2px', fontSize: 14, color: 'var(--t-00695c)', borderBottom: '1px solid var(--c-e0f2f1)', paddingBottom: 4 },
  label: { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '10px 0 4px' },
  labelHint: { textTransform: 'none', letterSpacing: 0, fontWeight: 400 },
  input: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 14, fontFamily: 'inherit', marginBottom: 2 },
  twoCol: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  pills: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 },
  check: { display: 'inline-flex', gap: 6, alignItems: 'center', padding: '5px 10px', borderRadius: 16, border: '1.5px solid var(--c-cfd8dc)', fontSize: 13, color: 'var(--t-455a64)', cursor: 'pointer' },
  checkOn: { border: '1.5px solid var(--c-00897b)', background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 14, background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: 'inherit', margin: '2px 4px 2px 0' },
  chipX: { border: 'none', background: 'none', color: 'var(--t-00695c)', fontSize: 15, cursor: 'pointer', padding: 0, lineHeight: 1 },
  matches: { border: '1px solid var(--c-e0e0e0)', borderRadius: 8, marginTop: 4, overflow: 'hidden' },
  match: { display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', borderBottom: '1px solid var(--c-f5f5f5)', background: 'var(--c-ffffff)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', overflowWrap: 'anywhere' },
  schedBox: { marginTop: 14, border: '1.5px solid var(--c-b2dfdb)', borderRadius: 10, padding: '12px 14px', fontSize: 14, color: 'var(--t-263238)', display: 'flex', flexDirection: 'column', gap: 8 },
  repeatRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  repeatOpt: { padding: '6px 12px', borderRadius: 999, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  repeatOn: { borderColor: 'var(--c-00897b)', background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)' },
  dateInput: { padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 14, fontFamily: 'inherit', maxWidth: 260 },
  countBox: { marginTop: 14, background: 'var(--c-f1f8f6)', borderRadius: 10, padding: '10px 12px', fontSize: 14, color: 'var(--t-263238)', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' },
  warn: { color: 'var(--t-b71c1c)', fontSize: 13 },
  excluded: { fontSize: 12, color: 'var(--t-607d8b)' },
  peopleList: { width: '100%', boxSizing: 'border-box', maxHeight: 300, overflowY: 'auto', border: '1px solid var(--c-eceff1)', borderRadius: 8, background: 'var(--c-ffffff)', marginTop: 6 },
  personRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--c-f5f5f5)', fontSize: 13, overflowWrap: 'anywhere' },
  dim: { color: 'var(--t-78909c)', fontSize: 12 },
  err: { color: 'var(--t-c62828)', fontSize: 12 },
  problems: { marginTop: 10, background: 'var(--c-fff3e0)', color: 'var(--t-8d4a00)', borderRadius: 8, padding: '8px 12px', fontSize: 13, lineHeight: 1.6 },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 },
  saveBtn: { padding: '9px 16px', borderRadius: 8, border: '1.5px solid var(--c-00897b)', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  sendBtn: { padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  ghostBtn: { padding: '8px 14px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', color: 'var(--t-455a64)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  linkBtn: { padding: '4px 6px', border: 'none', background: 'none', color: 'var(--t-00897b)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' },
  dangerLink: { padding: '4px 6px', border: 'none', background: 'none', color: 'var(--t-c62828)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' },
  confirm: { marginTop: 14, border: '2px solid var(--c-00897b)', borderRadius: 10, padding: '12px 14px', background: 'var(--c-f1f8f6)', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 },
  liveTitle: { fontSize: 11, fontWeight: 700, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '10px 0 6px', display: 'flex', gap: 8, alignItems: 'center' },
  unsaved: { padding: '1px 8px', borderRadius: 10, background: 'var(--c-fff3e0)', color: 'var(--t-e65100)', fontSize: 10, textTransform: 'none', letterSpacing: 0 },
  sentTitle: { margin: '0 0 4px', fontSize: 17, color: 'var(--t-263238)' },
  progressBox: { margin: '12px 0', border: '1px solid var(--c-e0f2f1)', borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  progressLine: { display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 14, flexWrap: 'wrap' },
  bar: { height: 10, borderRadius: 6, background: 'var(--c-eceff1)', overflow: 'hidden' },
  barFill: { height: '100%', background: 'var(--c-00897b)', transition: 'width .3s' },
  filterRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  filter: { padding: '5px 12px', borderRadius: 16, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  filterOn: { background: 'var(--c-00695c)', border: '1.5px solid var(--c-00695c)', color: 'var(--t-ffffff)' },
};
